import React from 'react';
import { analytics } from 'src/utils/analytics';

export interface Params {
  containerId?: string;
  slotPath?: string;
  slotSize: [number, number] | [number, number][];
  targeting?: [string, string[]];
  closable?: boolean;
}

export type DfpInitStatus = 'pending' | 'resolved' | 'timeout' | 'failed';

const INTERSTITIAL_INIT_TIMEOUT_MS = 5000;
const VIEWABILITY_TIMEOUT_MS = 4000;

const initializedContainerIds = new Set<string>();

function findExistingSlot(containerId: string): googletag.Slot | null {
  const googletag = window.googletag;
  if (!googletag?.pubads) {
    return null;
  }

  return (
    googletag
      .pubads()
      .getSlots()
      .find(slot => slot.getSlotElementId() === containerId) ?? null
  );
}

export function useDFP(params: Params) {
  const { containerId, slotPath, slotSize, targeting } = params;
  const [isClosed, setIsClosed] = React.useState(false);
  const [isRendered, setIsRendered] = React.useState(false);
  const [isViewable, setIsViewable] = React.useState(false);
  const [slotResolved, setSlotResolved] = React.useState(false);
  const [initStatus, setInitStatus] = React.useState<DfpInitStatus>('pending');
  const mountGenerationRef = React.useRef(0);

  const minWidth =
    Array.isArray(slotSize[0]) === false
      ? slotSize[0]
      : (slotSize as number[][]).reduce((acc, [value]) => {
          return value < acc ? value : acc;
        }, (slotSize as number[][])[0][0]);
  const minHeight =
    Array.isArray(slotSize[0]) === false
      ? slotSize[1]
      : (slotSize as number[][]).reduce((acc, [, value]) => {
          return value < acc ? value : acc;
        }, (slotSize as number[][])[0][1]);

  React.useLayoutEffect(() => {
    if (isClosed || !containerId || !slotPath) {
      return;
    }

    const slotNode = document.getElementById(containerId);
    if (!slotNode?.isConnected) {
      return;
    }

    let mounted = true;
    const mountGeneration = mountGenerationRef.current + 1;
    mountGenerationRef.current = mountGeneration;
    const controller = new AbortController();
    const googletag = window.googletag;
    const topWindow = window.top as typeof window & { DFP?: { onMaavaronClose?: () => void } };
    let slotEventResolved = false;
    let viewabilityTimeoutId: number | undefined;
    let isViewableRef = false;

    const clearViewabilityTimeout = () => {
      if (viewabilityTimeoutId !== undefined) {
        window.clearTimeout(viewabilityTimeoutId);
        viewabilityTimeoutId = undefined;
      }
    };

    const markViewable = () => {
      if (!mounted || isViewableRef) {
        return;
      }

      isViewableRef = true;
      clearViewabilityTimeout();
      setIsViewable(true);
    };

    const dismissBrokenCreative = () => {
      clearViewabilityTimeout();
      analytics.gtag.event('ad_script_failed');
      setIsClosed(true);
    };

    const finishAsEmpty = (status: DfpInitStatus) => {
      if (!mounted || slotEventResolved) {
        return;
      }

      slotEventResolved = true;
      clearViewabilityTimeout();
      setSlotResolved(true);
      setIsRendered(false);
      setIsViewable(false);
      setInitStatus(status);
    };

    const markResolved = (rendered: boolean) => {
      if (!mounted || slotEventResolved) {
        return;
      }

      slotEventResolved = true;
      setSlotResolved(true);
      setIsRendered(rendered);
      setInitStatus('resolved');

      if (!rendered) {
        setIsViewable(false);
        return;
      }

      viewabilityTimeoutId = window.setTimeout(() => {
        if (!mounted || isViewableRef) {
          return;
        }

        dismissBrokenCreative();
      }, VIEWABILITY_TIMEOUT_MS);
    };

    const markFailed = () => {
      analytics.gtag.event('ad_script_failed');
      finishAsEmpty('failed');
    };

    const markTimeout = () => {
      analytics.gtag.event('ad_initialization_timeout');
      finishAsEmpty('timeout');
    };

    if (!googletag) {
      markFailed();
      return () => {
        mounted = false;
        controller.abort();
        clearViewabilityTimeout();
      };
    }

    if (params.closable) {
      topWindow.DFP ??= {};
      topWindow.DFP.onMaavaronClose = function () {
        setIsClosed(true);
      };
    }

    window.addEventListener(
      'DFPDecision',
      (e: Event) => {
        const [, , isClosedEvent] = (e as CustomEvent).detail;
        setIsClosed(isClosedEvent);
      },
      { signal: controller.signal },
    );

    const onCreativeError = (event: ErrorEvent) => {
      if (!isViewableRef && slotEventResolved) {
        const message = event.message ?? '';
        if (message.includes('classList') || message.includes('context')) {
          dismissBrokenCreative();
        }
      }
    };

    window.addEventListener('error', onCreativeError, { signal: controller.signal });

    const timeoutId = window.setTimeout(markTimeout, INTERSTITIAL_INIT_TIMEOUT_MS);

    const attachSlotListeners = () => {
      const onSlotRenderEnded = (event: googletag.events.SlotRenderEndedEvent) => {
        if (event.slot.getSlotElementId() !== containerId) {
          return;
        }

        console.info('onSlotRenderEnded:', {
          isEmpty: event.isEmpty,
          advertiserId: event.advertiserId,
          campaignId: event.campaignId,
          creativeId: event.creativeId,
          lineItemId: event.lineItemId,
        });

        window.clearTimeout(timeoutId);

        if (event.isEmpty) {
          analytics.gtag.event('ad_slot_empty');
          markResolved(false);
          return;
        }

        markResolved(true);
      };

      const onImpressionViewable = (event: googletag.events.ImpressionViewableEvent) => {
        if (event.slot.getSlotElementId() !== containerId) {
          return;
        }

        markViewable();
      };

      googletag.pubads().addEventListener('slotRenderEnded', onSlotRenderEnded);
      googletag.pubads().addEventListener('impressionViewable', onImpressionViewable);
    };

    googletag.cmd.push(() => {
      const slotNode = document.getElementById(containerId);

      if (!slotNode?.isConnected) {
        window.clearTimeout(timeoutId);
        markFailed();
        return;
      }

      attachSlotListeners();

      const existingSlot = findExistingSlot(containerId);
      if (existingSlot) {
        googletag.display(containerId);
        initializedContainerIds.add(containerId);
        return;
      }

      const definedSlot = googletag.defineSlot(slotPath, slotSize, containerId);

      if (!definedSlot) {
        window.clearTimeout(timeoutId);
        markFailed();
        return;
      }

      const activeSlot = definedSlot.addService(googletag.pubads());
      activeSlot.setTargeting('MAKOPAGE', 'EX_Compass-Election');

      if (targeting) {
        activeSlot.setTargeting(...targeting);
      }

      googletag.pubads().enableSingleRequest();
      googletag.enableServices();
      googletag.display(containerId);
      initializedContainerIds.add(containerId);
    });

    return () => {
      mounted = false;
      window.clearTimeout(timeoutId);
      clearViewabilityTimeout();
      controller.abort();

      googletag?.cmd.push(() => {
        if (mountGenerationRef.current !== mountGeneration) {
          return;
        }

        const slot = findExistingSlot(containerId);
        if (slot) {
          googletag.destroySlots([slot]);
        }
        initializedContainerIds.delete(containerId);
      });
    };
  }, [isClosed, containerId, slotPath, slotSize, targeting, params.closable]);

  return {
    isClosed,
    isRendered,
    isViewable,
    slotResolved,
    initStatus,
    minWidth,
    minHeight,
    close: () => setIsClosed(true),
  };
}
